import React, { useState, useCallback, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { BASE_URL } from "../../config";
import { getAuthHeaders } from "../../services/useAuth";

// ── Endpoints ────────────────────────────────────────────────
const ENDPOINTS = [
  {
    key: "states",
    label: "States",
    path: "/api/bulk-upload/states",
    icon: "🗺️",
    color: "#6366f1",
    requiredFields: ["name", "code"],
  },
  {
    key: "cities",
    label: "Cities",
    path: "/api/bulk-upload/cities",
    icon: "🏙️",
    color: "#0ea5e9",
    requiredFields: ["name", "state_id"],
  },
  {
    key: "products",
    label: "Products",
    path: "/api/bulk-upload/temptation",
    icon: "📦",
    color: "#84cc16",
    requiredFields: ["name", "category_id"],
  },
  {
    key: "temptationScheme",
    label: "Temptation Scheme",
    path: "/api/bulk-upload/create-scheme-temptation",
    icon: "🎯",
    color: "#f97316",
    requiredFields: ["name", "product_id", "scheme_type"],
  },
  {
    key: "employees",
    label: "Employees",
    path: "/api/bulk-upload/employees",
    icon: "👥",
    color: "#10b981",
    requiredFields: ["name", "email", "phone"],
  },
  {
    key: "expenses",
    label: "Expenses",
    path: "/api/bulk-upload/expenses",
    icon: "💰",
    color: "#f59e0b",
    requiredFields: ["amount", "date", "category"],
  },
  {
    key: "areas",
    label: "Areas",
    path: "/api/bulk-upload/areas",
    icon: "📍",
    color: "#ef4444",
    requiredFields: ["name", "city_id"],
  },
  {
    key: "routes",
    label: "Routes",
    path: "/api/bulk-upload/routes",
    icon: "🛣️",
    color: "#8b5cf6",
    requiredFields: ["name", "area_id"],
  },
  {
    key: "chemists",
    label: "Chemists",
    path: "/api/bulk-upload/chemists",
    icon: "⚗️",
    color: "#06b6d4",
    requiredFields: ["name", "phone", "area_id"],
  },
  {
    key: "doctors",
    label: "Doctors",
    path: "/api/bulk-upload/doctors",
    icon: "🩺",
    color: "#ec4899",
    requiredFields: ["name", "specialization", "phone"],
  },
  {
    key: "stockists",
    label: "Stockists",
    path: "/api/bulk-upload/stockists",
    icon: "📦",
    color: "#84cc16",
    requiredFields: ["name", "phone", "area_id"],
  },
  {
    key: "stockists_assign",
    label: "Stockist Assignment",
    path: "/api/bulk-upload/stockists/assign",
    icon: "🔗",
    color: "#f97316",
    requiredFields: ["stockist_id", "employee_id"],
  },
];

// ── Parse rows from sheet ─────────────────────────────────────
function parseRows(allRows) {
  if (!allRows.length) return { headers: [], rows: [] };
  const headers = allRows[0].map((h) =>
    String(h ?? "").trim().replace(/^\uFEFF/, "")
  );
  const rows = allRows.slice(1).map((cells, idx) => ({
    rowNumber: idx + 2,
    cells,
  }));
  return { headers, rows };
}

const BulkUpload = () => {
  const [selectedEndpoint, setSelectedEndpoint] = useState(ENDPOINTS[0].key);
  const [file, setFile] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState("");
  const [previewData, setPreviewData] = useState([]);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [apiResponse, setApiResponse] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const fileInputRef = React.useRef();
  const currentEndpoint = ENDPOINTS.find((ep) => ep.key === selectedEndpoint);

  // ── File select ───────────────────────────────────────────
  const handleFileUpload = useCallback((event) => {
    const f = event.target.files[0];
    if (!f) return;

    setFile(f);
    setUploadStatus(null);
    setApiResponse(null);
    setPreviewData([]);

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const wb = XLSX.read(data, { type: "array", codepage: 65001 });
      setSheets(wb.SheetNames);
      setSelectedSheet(wb.SheetNames[0] || "");
    };
    reader.readAsArrayBuffer(f);
  }, []);

  // ── Load sheet preview ────────────────────────────────────
  const loadSheetPreview = useCallback(() => {
    if (!file || !selectedSheet) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const wb = XLSX.read(data, { type: "array", codepage: 65001 });
      const ws = wb.Sheets[selectedSheet];
      setPreviewData(XLSX.utils.sheet_to_json(ws, { header: 1, defval: null }));
    };
    reader.readAsArrayBuffer(file);
  }, [file, selectedSheet]);

  useEffect(() => {
    if (file && selectedSheet) loadSheetPreview();
  }, [selectedSheet]);

  // ── Upload ────────────────────────────────────────────────
  const handleUpload = async () => {
    if (!file) return alert("Please select a file");

    const endpoint = ENDPOINTS.find((ep) => ep.key === selectedEndpoint);
    const formData = new FormData();
    formData.append("file", file);

    setUploadStatus("uploading");
    setApiResponse(null);

    try {
      const response = await axios.post(
        `${BASE_URL}${endpoint.path}`,
        formData,
        { headers: { Authorization: getAuthHeaders().Authorization } },
      );
      setApiResponse(response.data);
      setUploadStatus("success");
    } catch (error) {
      setApiResponse(error.response?.data || null);
      setUploadStatus("error");
    }
  };

  // ── Merge API errors into rows ────────────────────────────
  const { headers, rows } = parseRows(previewData);

  // Build lookup maps from API response (only used after upload)
  const srvDupMap = {};
  const srvErrMap = {};

  if (apiResponse) {
    (apiResponse?.duplicates ?? []).forEach((d) => {
      if (d.row != null) srvDupMap[d.row] = d;
    });
    (apiResponse?.errors ?? []).forEach((e) => {
      if (e.row != null) {
        if (!srvErrMap[e.row]) srvErrMap[e.row] = [];
        srvErrMap[e.row].push(e);
      }
    });
  }

  const displayRows = rows.map((r) => ({
    ...r,
    serverErrors: apiResponse ? srvErrMap[r.rowNumber] || [] : [],
    isServerDup: apiResponse ? !!srvDupMap[r.rowNumber] : false,
    // A row is "OK" only when upload succeeded and server reported no issues
    isOk:
      uploadStatus === "success" &&
      !srvDupMap[r.rowNumber] &&
      !srvErrMap[r.rowNumber]?.length,
  }));

  const hasIssuesCol = uploadStatus === "success" || uploadStatus === "error";

  // ── Summary counters (only after upload) ─────────────────
  const totalInserted = apiResponse?.inserted ?? apiResponse?.success ?? null;
  const totalDups = (apiResponse?.duplicates ?? []).length;
  const totalErrors = (apiResponse?.errors ?? []).length;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* HEADER */}
      <div className="flex items-center gap-4 mb-6">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center text-xl text-white shadow-md"
          style={{ background: currentEndpoint.color }}
        >
          📊
        </div>
        <div>
          <h1 className="text-2xl font-bold">Bulk Data Upload</h1>
          <p className="text-sm text-gray-500">
            Select module → Upload file → Validate → Submit
          </p>
        </div>
      </div>

      {/* CONFIG CARD */}
      <div className="bg-white border rounded-xl p-6 shadow-sm mb-6">
        {/* Module select */}
        <div className="mb-6">
          <label className="block text-xs font-semibold text-gray-500 mb-2">
            Select Module
          </label>
          <select
            value={selectedEndpoint}
            onChange={(e) => {
              setSelectedEndpoint(e.target.value);
              setUploadStatus(null);
              setApiResponse(null);
            }}
            className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
          >
            {ENDPOINTS.map((ep) => (
              <option key={ep.key} value={ep.key}>
                {ep.icon} {ep.label}
              </option>
            ))}
          </select>
        </div>

        {/* Sheet select (if multiple sheets) */}
        {sheets.length > 1 && (
          <div className="mb-6">
            <label className="block text-xs font-semibold text-gray-500 mb-2">
              Select Sheet
            </label>
            <select
              value={selectedSheet}
              onChange={(e) => setSelectedSheet(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
            >
              {sheets.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Dropzone */}
        <div
          onClick={() => fileInputRef.current.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleFileUpload({ target: { files: [e.dataTransfer.files[0]] } });
          }}
          className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition
            ${dragOver ? "border-indigo-500 bg-indigo-50" : "border-gray-300 bg-gray-50"}`}
        >
          <div className="text-4xl mb-3">{file ? "✅" : "📁"}</div>
          <p className="font-semibold">
            {file ? file.name : "Click or drag & drop file here"}
          </p>
          <p className="text-xs text-gray-400 mt-2">
            Supports .xlsx · .xls · .csv
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFileUpload}
            hidden
          />
        </div>

        {/* Upload button */}
        <button
          onClick={handleUpload}
          disabled={!file || uploadStatus === "uploading"}
          className="mt-6 px-6 py-3 rounded-lg font-semibold text-white shadow-md disabled:bg-gray-300"
          style={{ background: currentEndpoint.color }}
        >
          {uploadStatus === "uploading"
            ? "Uploading..."
            : `Upload ${currentEndpoint.label}`}
        </button>
      </div>

      {/* API RESPONSE SUMMARY */}
      {apiResponse && (
        <div
          className={`mb-4 px-5 py-4 rounded-xl border text-sm font-medium flex flex-wrap gap-6 items-center
          ${uploadStatus === "success"
              ? "bg-green-50 border-green-200 text-green-700"
              : "bg-red-50 border-red-200 text-red-700"
            }`}
        >
          <span>
            {uploadStatus === "success"
              ? "✅ Upload complete"
              : "❌ Upload finished with errors"}
          </span>
          {totalInserted != null && (
            <span>
              Inserted: <strong>{totalInserted}</strong>
            </span>
          )}
          {totalDups > 0 && (
            <span>
              Duplicates: <strong>{totalDups}</strong>
            </span>
          )}
          {totalErrors > 0 && (
            <span>
              Errors: <strong>{totalErrors}</strong>
            </span>
          )}
          {apiResponse?.message && (
            <span className="ml-auto text-xs opacity-70">
              {apiResponse.message}
            </span>
          )}
        </div>
      )}

      {/* PREVIEW TABLE */}
      {displayRows.length > 0 && (
        <div className="bg-white border rounded-xl shadow-sm overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3 text-left">#</th>
                {/* Status col only after upload */}
                {hasIssuesCol && (
                  <th className="px-4 py-3 text-left">Status</th>
                )}
                {headers.map((h) => (
                  <th key={h} className="px-4 py-3 text-left">
                    {h}
                  </th>
                ))}
                {/* Issues col only after upload */}
                {hasIssuesCol && (
                  <th className="px-4 py-3 text-left">Issues</th>
                )}
              </tr>
            </thead>
            <tbody>
              {displayRows.map((row) => (
                <tr
                  key={row.rowNumber}
                  className={`border-t transition
                    ${row.serverErrors.length > 0
                      ? "bg-red-50"
                      : row.isServerDup
                        ? "bg-yellow-50"
                        : row.isOk
                          ? "bg-green-50"
                          : "hover:bg-gray-50"
                    }`}
                >
                  <td className="px-4 py-2 text-gray-400 font-mono">
                    {row.rowNumber}
                  </td>

                  {/* Status badge — only after upload */}
                  {hasIssuesCol && (
                    <td className="px-4 py-2 whitespace-nowrap">
                      {row.serverErrors.length > 0 ? (
                        <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-600 font-semibold">
                          ✗ Error
                        </span>
                      ) : row.isServerDup ? (
                        <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700 font-semibold">
                          ⚠ Duplicate
                        </span>
                      ) : row.isOk ? (
                        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700 font-semibold">
                          ✓ Inserted
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-500 font-semibold">
                          — Pending
                        </span>
                      )}
                    </td>
                  )}

                  {/* Data cells */}
                  {headers.map((h, i) => (
                    <td key={h} className="px-4 py-2">
                      {row.cells[i] != null && row.cells[i] !== "" ? (
                        row.cells[i]
                      ) : (
                        <span className="text-gray-300 text-xs italic">—</span>
                      )}
                    </td>
                  ))}

                  {/* Issues — only after upload, only from API */}
                  {hasIssuesCol && (
                    <td className="px-4 py-2 max-w-xs">
                      {row.serverErrors.length > 0 ? (
                        row.serverErrors.map((e, i) => (
                          <span
                            key={i}
                            className="inline-block bg-red-100 text-red-700 text-xs px-2 py-1 rounded mr-1 mb-1"
                          >
                            {e.message || e.reason || JSON.stringify(e)}
                          </span>
                        ))
                      ) : row.isServerDup ? (
                        <span className="inline-block bg-yellow-100 text-yellow-700 text-xs px-2 py-1 rounded">
                          Already exists in database
                        </span>
                      ) : null}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-3 text-xs text-gray-400 border-t">
            {displayRows.length} rows previewed
            {hasIssuesCol &&
              ` · ${displayRows.filter((r) => r.isOk).length} inserted · ${displayRows.filter((r) => r.isServerDup).length} duplicates · ${displayRows.filter((r) => r.serverErrors.length > 0).length} errors`}
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkUpload;
